import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail, AUTH_EXPIRED_EVENT } from "../lib/api";
import { setQueueUser } from "../lib/queueUser";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = noch am Prüfen, false = nicht eingeloggt, object = User
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setQueueUser(data.id);
            setUser(data);
            return data;
        } catch {
            setQueueUser(null);
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // R14: 401 aus beliebigen API-Calls -> erst Token-Refresh versuchen
    // (Refresh-Cookie lebt 14 Tage), sonst user=false -- der Router zeigt
    // dann den Login statt einer stumm kaputten UI.
    useEffect(() => {
        let running = false;
        const onExpired = async () => {
            if (running) return;
            running = true;
            try {
                await api.post("/auth/refresh");
                await refresh();
            } catch {
                setQueueUser(null);
                setUser(false);
            } finally {
                running = false;
            }
        };
        window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
    }, [refresh]);

    const signIn = useCallback(
        async ({ email, password }) => {
            try {
                const { data } = await api.post("/auth/login", { email, password });
                setQueueUser(data.user?.id);
                setUser(data.user);
                return { ok: true, user: data.user };
            } catch (e) {
                return {
                    ok: false,
                    error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
                };
            }
        },
        []
    );

    const signOut = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore */
        }
        // #53: Queue-Besitzer räumen -- Worker versenden abgemeldet nichts,
        // Einträge bleiben liegen, bis derselbe Nutzer sich wieder anmeldet.
        setQueueUser(null);
        setUser(false);
    }, []);

    // #46: Profil-Self-Service (Anzeigename) meldet die Server-Antwort direkt
    // in den Kontext, damit Dashboard-Gruß und Shell ohne Reload nachziehen.
    // Nur bei aktiver Sitzung -- ein abgemeldeter Zustand bleibt unberührt.
    const updateUser = useCallback((patch) => {
        setUser((prev) => (prev && prev !== false ? { ...prev, ...patch } : prev));
    }, []);

    const value = {
        user,
        role: user && user !== false ? user.role : null,
        isLoading: user === null,
        isAuthenticated: !!user && user !== false,
        signIn,
        signOut,
        refresh,
        updateUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
